import { useState } from 'react'
import cx from 'classnames'

import type {
  SanityProductGalleryPhoto,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import type { SanityImageFragment } from '@data/sanity/queries/types/image'
import { getProductGalleryPhotos } from '@lib/product-images'

import Photo from '@components/photo'
import Icon from '@components/icon'
import Carousel from '@components/carousel'

interface ProductGalleryProps {
  photosets: SanityProductGalleryPhoto[]
  activeVariant: SanityProductVariantFragment
  previewVariant?: SanityProductVariantFragment
  hasArrows?: boolean
  hasDots?: boolean
  hasDrag?: boolean
  hasCounter?: boolean
  preloadFirstPhoto?: boolean
}

const ProductGallery = ({
  photosets,
  activeVariant,
  previewVariant,
  preloadFirstPhoto = false,
}: ProductGalleryProps) => {
  const [fullScreenPhoto, setFullscreenPhoto] =
    useState<SanityImageFragment | null>()

  // Preview variant photos and active variant photos
  const previewVariantPhotos = previewVariant
    ? getProductGalleryPhotos(photosets, previewVariant)
    : []
  const photos =
    previewVariantPhotos.length > 0
      ? previewVariantPhotos
      : getProductGalleryPhotos(photosets, activeVariant)

  const openFullscreen = (photo: SanityImageFragment | null) => {
    document.body.classList.add('overflow-y-hidden')
    setFullscreenPhoto(photo)
  }

  const closeFullscreen = () => {
    setFullscreenPhoto(null)
    document.body.classList.remove('overflow-y-hidden')
  }

  return (
    <>
      {!!fullScreenPhoto && (
        <div
          className="fixed top-0 left-0 right-0 bg-pageBG w-full h-screen z-80 overflow-y-auto flex items-start"
          onKeyUp={(event) => {
            if (event.key === 'Escape') {
              closeFullscreen()
            }
          }}
        >
          <button onClick={closeFullscreen} className="w-full">
            <Photo
              image={fullScreenPhoto}
              className="w-full"
              imageClassName="w-full"
              fadeIn
            />
          </button>

          <button
            className="fixed top-0 right-0 uppercase text-xs p-6 inline-flex items-center gap-x-2"
            onClick={closeFullscreen}
          >
            {/* TODO: Move text to Sanity. */}
            Close
            <Icon id="product-gallery" name="Cross" />
          </button>
        </div>
      )}

      <div className="hidden sm:flex flex-col h-full overflow-y-scroll no-scrollbar">
        {photos.map((photo, index) => (
          <button
            className={cx('w-full')}
            key={photo.asset?.assetId}
            onClick={() => {
              openFullscreen(photo)
            }}
          >
            <Photo
              image={photo}
              priority={preloadFirstPhoto && index === 0}
              className="w-full"
              imageClassName="w-full"
              fadeIn
            />
          </button>
        ))}
      </div>

      <Carousel
        hasArrows
        className="sm:hidden"
        options={{
          slides: {
            spacing: 0,
            perView: 1,
          },
        }}
        items={photos.map((photo, index) => (
          <button
            key={index}
            onClick={() => {
              openFullscreen(photo)
            }}
          >
            <Photo image={photo} priority={preloadFirstPhoto && index === 0} />
          </button>
        ))}
      />
    </>
  )
}

export default ProductGallery
